// combatpro/conditions/conditionsEngine.js
//
// Combat PRO - Conditions Engine (Stage 41: pure conditions metadata + lookup).
//
// Pure, UI-independent conditions metadata and lookup logic extracted from
// battletracker.html (the read-only master spec). This module knows nothing
// about React, state, components, context, the Effects field, the conditions
// panel, history, or the DOM. It contains only deterministic condition metadata
// (descriptions, canonical keywords) and pure lookup/normalisation helpers. It
// receives strings and returns strings/data; it owns no state and performs no
// side effects.
//
// ARCHITECTURAL NOTE (recorded per the Stage 41 roadmap revision): the legacy
// "Conditions" slide-in panel is NOT a standalone runtime. It is a DISPLAY/REMOVE
// consumer of the Effects system - updateConditionsPanel (9112) aggregates each
// monster's `effect` field, and removeCondition (9166) clears that `effect`. The
// `monster.conditions[]` array is vestigial (no code populates it). Therefore the
// only genuinely independent, deferred-runtime-free Conditions logic is the
// metadata + normalisation + lookup captured here. The conditions PANEL is
// deferred until the Effects Runtime exists. Revised remaining order:
//   6. Conditions Engine (pure metadata)  <- THIS stage
//   7. Effects Runtime
//   8. Conditions Panel Integration
//   9. Morale Runtime
//   10. Spell Runtime Integration
//   11. Encounter Runtime
//
// Source mapping (battletracker.html):
//   - CONDITION_DESCRIPTIONS    line 9252. Reproduced verbatim (14 entries,
//                               capitalised display-name keys), as read by
//                               showConditionTooltip (9269-9270).
//   - CONDITION_KEYWORDS        line 4088 (VI_CONDITION_KEYWORDS). The 14 canonical
//                               lowercase condition keywords, in source order. The
//                               vi_ prefix is dropped to match the engine naming
//                               convention (cf. diceEngine rollFormula,
//                               initiativeEngine getAbilityModifier).
//   - normalizeCondition()      line 4321 (vi_normalizeCondition). Exact port:
//                               non-string passes through as (condition || null);
//                               otherwise lower-cases + trims and maps synonyms to
//                               the canonical form, falling back to the raw input.
//   - getConditionDescription() new pure lookup wrapping the
//                               CONDITION_DESCRIPTIONS[name] access from
//                               showConditionTooltip (9270). Returns the
//                               description string, or null when unknown (the
//                               source did `if (!desc) return;`).
//
// FORBIDDEN here and absent: React, components, context, DOM, getElementById,
// innerHTML, document, window, localStorage, API, fetch, websocket, history,
// rendering, the conditions panel, the Effects field, side effects. No optional
// chaining. No em dash.

// 14 condition descriptions, keyed by capitalised display name. Verbatim from the
// source CONDITION_DESCRIPTIONS (9252-9266). Used for tooltip / reference display.
export const CONDITION_DESCRIPTIONS = {
  Blinded: "Attack rolls have disadvantage. Attack rolls against you have advantage. You can't see and auto-fail sight-based checks.",
  Charmed: "Can't attack charmer or target them with harmful abilities. Charmer has advantage on social checks.",
  Deafened: "Can't hear and auto-fail hearing-based checks.",
  Frightened: "Disadvantage on ability checks and attack rolls while source is in sight. Can't willingly move closer to source.",
  Grappled: "Speed becomes 0. Condition ends if grappler is incapacitated or forced away.",
  Incapacitated: "Can't take actions or reactions.",
  Invisible: "Impossible to see without special sense. Attack rolls have advantage. Attack rolls against you have disadvantage.",
  Paralyzed: "Incapacitated, can't move or speak. Auto-fail STR/DEX saves. Attacks against you have advantage. Hits within 5ft are auto-crits.",
  Petrified: "Transformed to stone. Incapacitated, can't move/speak. Resistant to all damage. Immune to poison/disease.",
  Poisoned: "Disadvantage on attack rolls and ability checks.",
  Prone: "Disadvantage on attack rolls. Attacks against you have advantage if within 5ft, disadvantage if farther. Costs half movement to stand.",
  Restrained: "Speed becomes 0. Attack rolls have disadvantage. Attacks against you have advantage. Disadvantage on DEX saves.",
  Stunned: "Incapacitated, can't move. Can speak falteringly. Auto-fail STR/DEX saves. Attack rolls against you have advantage.",
  Unconscious: "Incapacitated, can't move/speak, unaware. Drop held items, fall prone. Auto-fail STR/DEX saves. Attacks have advantage. Hits within 5ft are auto-crits."
};

// The 14 canonical condition keywords (lowercase), in source order. Verbatim from
// VI_CONDITION_KEYWORDS (4088).
export const CONDITION_KEYWORDS = [
  "stunned", "paralyzed", "restrained", "blinded", "frightened", "charmed",
  "poisoned", "prone", "incapacitated", "petrified", "grappled", "deafened",
  "invisible", "unconscious"
];

// Normalise a free-form condition string to its canonical form. Exact port of
// vi_normalizeCondition (4321-4350): non-string input passes through as
// (condition || null); otherwise the input is lower-cased and trimmed, synonyms
// are mapped to the canonical condition, and anything unmapped falls back to the
// raw lower-cased value.
export function normalizeCondition(condition) {
  if (!condition || typeof condition !== "string") { return condition || null; }
  const raw = condition.toLowerCase().trim();
  const MAP = {
    blinded: "blinded", blind: "blinded",
    charmed: "charmed", charm: "charmed",
    deafened: "deafened", deaf: "deafened",
    frightened: "frightened", fear: "frightened", scared: "frightened",
    grappled: "grappled", grapple: "grappled",
    incapacitated: "incapacitated", incapacitate: "incapacitated",
    commanded: "incapacitated", confused: "incapacitated",
    compelled: "incapacitated", calmed: "incapacitated",
    invisible: "invisible",
    paralyzed: "paralyzed", paralyze: "paralyzed",
    petrified: "petrified", stone: "petrified",
    poisoned: "poisoned", poison: "poisoned",
    prone: "prone", knocked: "prone",
    restrained: "restrained", restrain: "restrained",
    stunned: "stunned", stun: "stunned",
    unconscious: "unconscious", asleep: "unconscious", sleep: "unconscious",
    // pass-through custom (non-crashing)
    hexed: "hexed", marked: "marked", baned: "baned",
    banished: "banished", levitated: "levitated",
    slowed: "slowed", outlined: "outlined", warded: "warded",
    blocked: "blocked", silenced: "silenced", suppressed: "suppressed",
    polymorphed: "polymorphed", possessed: "possessed",
    imprisoned: "imprisoned", enfeebled: "enfeebled", bound: "bound"
  };
  return MAP[raw] || raw;
}

// Look up a condition description by its capitalised display name. Mirrors the
// CONDITION_DESCRIPTIONS[condition] access in showConditionTooltip (9270);
// returns the description string, or null when the condition is unknown.
export function getConditionDescription(name) {
  const desc = CONDITION_DESCRIPTIONS[name];
  return desc ? desc : null;
}
